import type { z } from 'zod';
import { API_BASE_URL } from './env';
import { rememberReturnTo } from './auth';
import { ApiError, parseErrorEnvelope } from './errors';

export { ApiError };

export async function req<T = unknown>(
  path: string,
  init: RequestInit = {},
  schema?: z.ZodSchema<T>,
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers, credentials: 'include' });
  } catch (networkErr) {
    throw new ApiError(0, 'NETWORK', `network error: ${String(networkErr)}`);
  }

  if (res.status === 401) {
    // Covers both "no session" and "session with no role" — the API answers
    // 401 to each, so a customer who happens to hold a session sees the same
    // login page rather than a hint that the admin API exists.
    if (location.pathname !== '/login') {
      rememberReturnTo(location.pathname + location.search);
      location.href = '/login';
    }
    throw new ApiError(401, 'UNAUTHORIZED', 'redirecting to login');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw parseErrorEnvelope(res.status, body);
  }

  if (res.status === 204) return undefined as T;

  const json = await res.json();
  return schema ? schema.parse(json) : (json as T);
}
